import { DynDadosCadastraisProps } from ".";
import { Autocomplete, FormControl, FormHelperText, Grid, IconButton, InputAdornment, InputLabel, MenuItem, Select, TextField, Typography } from "@mui/material";
import { Controller, useFormContext } from "react-hook-form";
import { formatarCEP, permitirApenasNumerosOuSN } from "../../../../helpers/masks";
import { useMemo } from "react";
import { desformatarCEP } from "../../../../helpers/unmask";
import { enqueueSnackbar } from "notistack";
import axiosClient from "../../../../lib/axios";
import { ViaCEPResposta } from "../../../../types/externas/viacep-resultado";
import { useQuery } from "react-query";
import SearchIcon from '@mui/icons-material/Search';

export default function DadosEndereco({ spec, extras }: DynDadosCadastraisProps) {
    const { control, getFieldState, watch, getValues, setValue } = useFormContext();

    const ufs = useMemo(() => ["AC", "AL", "AP", "AM", "BA", "CE", "DF", "ES", "GO", "MA", "MT", "MS", "MG", "PA", "PB", "PR", "PE", "PI", "RJ", "RN", "RS", "RO", "RR", "SC", "SP", "SE", "TO"], [])

    //queries
    const { data } = useQuery({
        queryKey: ["pesquisar-cidades", watch(`${spec.name}.uf`)],
        queryFn: async () => {
            if (watch(`${spec.name}.uf`) === undefined) return []
            let result = await axiosClient.get("/pesquisar-cidades", {
                params: {
                    uf: getValues(`${spec.name}.uf`)
                }
            })
            return result?.data
        }
    });
    const { dataCidades }: ICidades = {
        dataCidades: data || []
    }
    //

    //funções manipuladoras
    const handlePesquisarCep = async () => {
        const { cep } = getValues(`${spec.name}`)
        if (cep === undefined || cep === "") return

        const unmaskCep = desformatarCEP(cep)

        if (unmaskCep.length !== 8) {
            enqueueSnackbar('CEP inválido. Deve conter 8 dígitos.', { variant: 'error' })
            return
        }

        try {
            const response = await axiosClient.get<ViaCEPResposta>(`https://viacep.com.br/ws/${unmaskCep}/json/`);
            const { bairro, logradouro, localidade, uf } = response.data
            setValue(`${spec.name}.uf`, uf)
            setValue(`${spec.name}.bairro`, bairro)
            setValue(`${spec.name}.cidade`, localidade)
            setValue(`${spec.name}.endereco`, logradouro)
        } catch (error) {
            enqueueSnackbar('Erro ao buscar endereço por CEP: ' + error, { variant: 'error' })
            return
        }
    }
    //
    return (
        <Grid container size={{ xs: 12 }} spacing={2}>
            <Grid size={{ xs: 12 }}>
                <Typography fontWeight={'bold'}>Endereço</Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
                <Controller
                    name={`${spec.name}.cep`}
                    control={control}
                    rules={{ required: 'Campo obrigatório' }}
                    render={({ field }) => (
                        <TextField
                            {...field}
                            label="CEP"
                            variant="outlined"
                            defaultValue={""}
                            fullWidth
                            required
                            onChange={(e) => field.onChange(formatarCEP(e.target.value))}
                            onBlur={handlePesquisarCep}
                            error={getFieldState(`${spec.name}.cep`).invalid}
                            helperText={getFieldState(`${spec.name}.cep`).error?.message || ""}
                            slotProps={{
                                input: {
                                    endAdornment: <InputAdornment position="end">
                                        <IconButton
                                            aria-label="toggle password visibility"
                                            onClick={handlePesquisarCep}
                                            edge="end"
                                        >
                                            <SearchIcon />
                                        </IconButton>
                                    </InputAdornment>
                                }
                            }}
                        />
                    )}
                />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
                <Controller
                    name={`${spec.name}.endereco`}
                    control={control}
                    rules={{ required: 'Campo obrigatório' }}
                    render={({ field }) => (
                        <TextField
                            {...field}
                            label="Endereço"
                            defaultValue={""}
                            variant="outlined"
                            required
                            fullWidth
                            error={getFieldState(`${spec.name}.endereco`).invalid}
                            helperText={getFieldState(`${spec.name}.endereco`).error?.message || ""}
                        />
                    )}
                />
            </Grid>

            <Grid size={{ xs: 12, md: 2 }}>
                <Controller
                    name={`${spec.name}.numero`}
                    control={control}
                    render={({ field }) => (
                        <TextField
                            {...field}
                            label="Número"
                            type="text"
                            variant="outlined"
                            defaultValue={""}
                            fullWidth
                            onChange={(e) => {
                                field.onChange(permitirApenasNumerosOuSN(e.target.value))
                            }}
                            error={getFieldState(`${spec.name}.numero`).invalid}
                            helperText={getFieldState(`${spec.name}.numero`).error?.message || ""}
                        />
                    )}
                />
            </Grid>

            <Grid size={{ xs: 12, md: 3 }}>
                <Controller
                    name={`${spec.name}.complemento`}
                    control={control}
                    render={({ field }) => (
                        <TextField
                            {...field}
                            label="Complemento"
                            defaultValue={""}
                            variant="outlined"
                            fullWidth
                            error={getFieldState(`${spec.name}.complemento`).invalid}
                            helperText={getFieldState(`${spec.name}.complemento`).error?.message || ""}
                        />
                    )}
                />
            </Grid>


            <Grid size={{ xs: 12, md: 3 }}>
                <Controller
                    name={`${spec.name}.bairro`}
                    control={control}
                    rules={{ required: 'Campo obrigatório' }}
                    render={({ field }) => (
                        <TextField
                            {...field}
                            label="Bairro"
                            defaultValue={""}
                            variant="outlined"
                            required
                            fullWidth
                            error={getFieldState(`${spec.name}.bairro`).invalid}
                            helperText={getFieldState(`${spec.name}.bairro`).error?.message || ""}
                        />
                    )}
                />
            </Grid>


            <Grid size={{ xs: 12, md: 2 }}>
                <Controller
                    name={`${spec.name}.uf`}
                    control={control}
                    rules={{ required: 'Campo obrigatório' }}
                    render={({ field }) => (
                        <FormControl fullWidth {...field} error={getFieldState(`${spec.name}.uf`).invalid} required>
                            <InputLabel id="uf-select">UF</InputLabel>
                            <Select
                                labelId="uf-select"
                                value={field.value}
                                label="UF"
                                onChange={(e) => field.onChange(e.target.value)}
                            >
                                {ufs.map((uf, index) => (
                                    <MenuItem key={index} value={uf}>{uf}</MenuItem>
                                ))}
                            </Select>
                            {getFieldState(`${spec.name}.uf`).error && (
                                <FormHelperText>{getFieldState(`${spec.name}.uf`).error?.message}</FormHelperText>
                            )}
                        </FormControl>
                    )}
                />
            </Grid>

            <Grid size={{ xs: 12, md: 4 }}>
                <Controller
                    name={`${spec.name}.cidade`}
                    control={control}
                    rules={{ required: 'Campo obrigatório' }}
                    render={({ field }) => (
                        <>
                            <Autocomplete
                                {...field}
                                fullWidth
                                disablePortal
                                options={dataCidades}
                                defaultValue={""}
                                onChange={(_, v) => field.onChange(v)}
                                renderInput={(params) =>
                                    <TextField
                                        {...params}
                                        label="Cidade"
                                        required
                                        defaultValue={""}
                                        error={Boolean(getFieldState(`${spec.name}.cidade`).invalid)}
                                        helperText={getFieldState(`${spec.name}.cidade`).error?.message || ""}
                                    />
                                }
                            />
                        </>
                    )}
                />
            </Grid>
        </Grid>
    )
}

interface ICidades {
    dataCidades: Array<string>
}